import { useCallback, useEffect } from 'react';

import { useDispatch, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { LicenseService } from '../components/services';
import {
  getCurrentUserElementCodes,
  getUserAsync,
  logoutAsync,
  setAuth,
  setIsLoaded
} from '../state/auth/authSlice';
import {
  setLicenseExpired,
  setLicenseInfo
} from '../state/license/licenseSlice';
import { AppDispatch, RootState } from '../state/store';

import useElementUIsService from './services/useElementUIsService';

export const useCheckAuth = () => {
  const store = useStore<RootState>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const elementUIsService = useElementUIsService();

  const checkLicenseFirst = useCallback(
    async (token: string | null): Promise<boolean> => {
      if (!token) return true;
      const resp = await LicenseService.getLicense();
      if (resp?.status === 403) {
        const data: any = resp.data;
        const text =
          typeof data === 'string'
            ? data
            : typeof data?.error === 'string'
            ? data.error
            : typeof data?.message === 'string'
            ? data.message
            : '';
        if (
          typeof text === 'string' &&
          text.toLowerCase().includes('licenseexpired')
        ) {
          dispatch(setLicenseExpired(true));
          await dispatch(logoutAsync());
          dispatch(setAuth(false));
          dispatch(setIsLoaded(true));
          navigate('/license-expired');
          return false;
        }
      }

      if (resp && resp.status < 400) {
        dispatch(setLicenseInfo(resp.data));
      }
      return true;
    },
    [dispatch, navigate]
  );

  const checkAuthStatus = useCallback(async () => {
    const { isAuth, isLoaded } = store.getState().auth;
    const token = localStorage.getItem('token');
    const loginUrl = '/login';
    const setPasswordUrl = '/set-password';
    const privacyPolicyUrl = '/privacy_policy';

    if (location.pathname === privacyPolicyUrl) {
      // Даже на этой странице, если есть сессия — проверяем лицензию первой
      const ok = await checkLicenseFirst(token);
      if (!ok) return;
      dispatch(setIsLoaded(true));
      return;
    }

    if (!isLoaded) {
      if (token) {
        // Проверка лицензии должна быть первой
        const ok = await checkLicenseFirst(token);
        if (!ok) return;
        dispatch(setAuth(true));

        // Получаем информацию о пользователе
        const userResult = await dispatch(getUserAsync());
        const user = userResult.payload as { isFirstLogin?: boolean };

        // Если пользователь должен установить пароль, редиректим на страницу установки пароля
        if (user?.isFirstLogin && location.pathname !== setPasswordUrl) {
          navigate(setPasswordUrl);
        } else if (location.pathname === loginUrl) {
          navigate('/');
        }

        await dispatch(getCurrentUserElementCodes(elementUIsService));
      } else navigate(loginUrl);
      dispatch(setIsLoaded(true));
      return;
    } else if (isAuth) {
      // Проверка лицензии должна быть первой
      const ok = await checkLicenseFirst(token);
      if (!ok) return;

      const userResult = await dispatch(getUserAsync());
      const user = userResult.payload as { isFirstLogin?: boolean };

      // Если пользователь должен установить пароль и не на странице установки пароля, редиректим
      if (
        user?.isFirstLogin &&
        location.pathname !== setPasswordUrl &&
        location.pathname !== loginUrl
      ) {
        navigate(setPasswordUrl);
      }

      await dispatch(getCurrentUserElementCodes(elementUIsService));
    }
  }, [
    checkLicenseFirst,
    dispatch,
    elementUIsService,
    location.pathname,
    navigate,
    store
  ]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      await Promise.resolve();
      if (ignore) {
        return;
      }
      await checkAuthStatus();
    })();
    return () => {
      ignore = true;
    };
  }, [checkAuthStatus]);
};
